import type { ShiftRecord } from '@libs/data-access/queries'
import { ConditionalWrap, Modal, Tooltip } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { ReduxState } from '@spa/redux/ReduxState'
import useMemoSelector from '@spa/redux/hooks/useMemoSelector'
import { LockedAlert } from '@spa/scenes/Plannings/Forms/components'
import color from 'color'
import moment from 'moment'
import React, { useState } from 'react'
import type { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getContractById } from 'snap-redux/modules/contracts/selectors'
import { getLabelById } from 'snap-redux/modules/labels/selectors'
import { getRestByOriginalShiftId } from 'snap-redux/modules/rests/selectors'
import { updateShift } from 'snap-redux/modules/shifts'
import { getWeeklyScheduleById } from 'snap-redux/modules/weeklyschedules/selectors'
import { bindPromiseActionCreators } from 'snap-redux/utils'

import { usePadSettings } from '../../hooks'
import { BlockHoursInfoLegacy } from '../RealHoursForms/ShiftRealHoursForm/components/BlockHoursInfo/BlockHoursInfoLegacy'
import { useRestTypeOptions } from '../RealHoursForms/ShiftRealHoursForm/hooks/useShiftRealHoursForm'
import { UnplannedBlockHoursInfoLegacy } from '../RealHoursForms/UnplannedShiftRealHoursForm/components/UnplannedBlockHoursInfo/UnplannedBlockHoursInfoLegacy'
import { HeaderInfo } from '../RealHoursForms/components'

import styles from './UnvalidateShiftForm.module.css'
import useUnvalidateShiftFormLegacy from './hooks'

type Props = {
  shift: ShiftRecord
  isManager: boolean
  canInvalidate: boolean
  formSuccess: (data) => void
}
/**
 * @deprecated use UnvalidateShiftForm
 */
const UnvalidateShiftFormLegacy: FC<Props> = ({
  isManager,
  canInvalidate,
  shift,
  formSuccess
}) => {
  const { t } = usePlanningFormTranslation()
  const [, setFormError] = useState(null)
  const dispatch = useDispatch()

  const contract = useMemoSelector((state: ReduxState) =>
    getContractById(state, shift.user_contract_id)
  )
  const restTypeOptions = useRestTypeOptions()
  const { methods, formData } = useUnvalidateShiftFormLegacy({ shift })
  const { handleSubmit, watch } = methods

  const { did_not_show, witheld_from_pay } = watch()

  const weeklyschedule = useMemoSelector(state =>
    getWeeklyScheduleById(state, shift.weeklyschedule_id)
  )
  const isPublished = weeklyschedule.published
  const labelId = shift.label_id

  const shiftLabel = useSelector((state: ReduxState) =>
    labelId ? getLabelById(state, labelId) : undefined
  )

  const labelColor = color(shiftLabel?.color).toString()

  const associatRestIdsSet = useSelector((state: ReduxState) =>
    shift?.id ? getRestByOriginalShiftId(state, shift.id) : undefined
  )

  const rest = associatRestIdsSet && associatRestIdsSet.first()

  // The default rest is: temporary_unavailability
  const shiftRest = rest?.rest_type
    ? restTypeOptions.find(restOption => restOption.value === rest?.rest_type)
    : restTypeOptions[1]

  const { hasSnapPad, hasSnapPadBreaksEnable } = usePadSettings()

  const shiftIsLocked = Boolean(shift.locked_at)
  const shiftValidated = shift?.validated_at !== null
  const validatorById = shift?.validator?.toJS()
  const shiftValidatedDate = moment(shift.validated_at).format(
    'dddd DD MMMM YYYY'
  )

  const bindedActions = bindPromiseActionCreators(
    {
      updateShift
    },
    dispatch
  )

  const onSubmit = () => {
    const shiftId = formData.id

    bindedActions.updateShift(shiftId, formData).then(
      data => {
        formSuccess(data)
      },
      error => {
        setFormError(error)
      }
    )
  }

  return (
    <>
      <Modal.Header title={t('unvalidateShiftForm.index.validatedShift')} />
      <form onSubmit={handleSubmit(onSubmit)} className='flex grow flex-col'>
        <Modal.Content>
          {shiftIsLocked && <LockedAlert />}
          <HeaderInfo
            membershipId={contract?.membership_id}
            date={shift.starts_at}
          />
          {shift?.planification_type === 'unscheduled' ? (
            <UnplannedBlockHoursInfoLegacy
              shift={shift}
              color={labelColor}
              isManager={isManager}
              hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
            />
          ) : (
            <BlockHoursInfoLegacy
              shift={shift}
              color={labelColor}
              hasSnapPad={hasSnapPad ?? false}
              didNotShow={did_not_show ?? false}
              witheldFromPay={witheld_from_pay ?? false}
              restType={shiftRest}
              restNote={rest?.note}
              isManager={isManager}
              hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
            />
          )}

          {shiftValidated && (
            <span className={styles.TopInfo}>
              {t('unvalidateShiftForm.index.validatedShiftBy', {
                user: validatorById,
                date: shiftValidatedDate,
                time: shift.validated_at
              })}
            </span>
          )}
        </Modal.Content>
        <Modal.Footer>
          {!shiftIsLocked && isManager && canInvalidate && (
            <ConditionalWrap
              condition={!isPublished}
              wrap={children => (
                <div className={styles.fill}>
                  <Tooltip
                    id='invalidateTooltip'
                    position='top'
                    label={t('unvalidateShiftForm.index.invalidateTooltip')}
                  >
                    <div>{children}</div>
                  </Tooltip>
                </div>
              )}
            >
              <Modal.Footer.Danger
                onClick={onSubmit}
                variant='secondary'
                fillWidth
                disabled={!isPublished}
              >
                {t('unvalidateShiftForm.index.invalidateShift')}
              </Modal.Footer.Danger>
            </ConditionalWrap>
          )}
        </Modal.Footer>
      </form>
    </>
  )
}

export default UnvalidateShiftFormLegacy
